import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@parrot-software/design-system';

interface ReportSelectOption {
  key: string;
  value: string;
}

interface ReportSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<ReportSelectOption>;
  placeholder?: string;
}

/**
 * ReportSelect - Select dropdown component for report filters
 *
 * Displays a dropdown select with options for report filters.
 * Handles translation of labels and placeholders automatically.
 * Layout wrapper should be handled by the parent component.
 *
 * @param {ReportSelectProps} props - Component properties
 * @param {string} props.label - Label for the select (translation key)
 * @param {string} props.value - Current selected value
 * @param {(value: string) => void} props.onChange - Callback when value changes
 * @param {ReadonlyArray<ReportSelectOption>} props.options - Array of selectable options
 * @param {string} [props.placeholder] - Placeholder text (translation key, optional)
 * @returns {React.ReactElement} Select component with label
 *
 * @example
 * <div className="flex flex-col gap-2">
 *   <ReportSelect
 *     label="reports:labels.provider"
 *     value="online"
 *     onChange={(value) => handleChange('provider', value)}
 *     options={[
 *       { key: 'online', value: 'Pedidos online' },
 *       { key: 'parrot', value: 'Punto de venta' },
 *     ]}
 *     placeholder="reports:placeholders.selectProvider"
 *   />
 * </div>
 */
export const ReportSelect = ({ label, value, onChange, options, placeholder }: ReportSelectProps) => {
  const { t } = useTranslation(['reports', 'reportSales']);
  const selectId = useId();

  return (
    <>
      <Label className="text-sm font-medium w-fit" htmlFor={selectId}>
        {t(label)}
      </Label>
      <Select onValueChange={onChange} value={value}>
        <SelectTrigger id={selectId}>
          <SelectValue placeholder={placeholder ? t(placeholder) : undefined} />
        </SelectTrigger>
        <SelectContent className="h-[240px] z-1350">
          {options.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
};
